import { wcStoreApi } from "../../../app/api";
import type { FetchBaseQueryMeta } from "@reduxjs/toolkit/query";
import type {
  RawProduct,
  RawProductsResponse,
} from "../../../types/api/wcStoreApi/wcStoreApiProductsEndpointTypes";
import type {
  GetProductsParams,
  TransformedProductsResult,
  TransformedProduct,
  TransformedPriceInfo,
  TransformedTermRef,
  TransformedProductInstructions,
  TransformedProductVideo,
} from "../types/productApiTypes";

// Helper function to transform a single RawProduct to TransformedProduct
const transformRawProduct = (rawProduct: RawProduct): TransformedProduct => {
  const prices: TransformedPriceInfo = {
    currencyCode: rawProduct.prices.currency_code,
    currencySymbol: rawProduct.prices.currency_symbol,
    currencyMinorUnit: rawProduct.prices.currency_minor_unit,
    price: rawProduct.prices.price,
    regularPrice: rawProduct.prices.regular_price,
    salePrice: rawProduct.prices.sale_price,
    onSale: rawProduct.on_sale,
  };

  const transformTerms = (
    terms: { id: number; name: string; slug: string; link: string }[]
  ): TransformedTermRef[] =>
    terms.map((term) => ({
      id: term.id,
      name: term.name,
      slug: term.slug,
      link: term.link,
    }));

  let transformedProductInstructions:
    | TransformedProductInstructions
    | undefined;
  if (rawProduct.extensions?.product_instructions?.instructions) {
    transformedProductInstructions = {
      instructions: rawProduct.extensions.product_instructions.instructions,
    };
  }

  let transformedProductVideo: TransformedProductVideo | undefined;
  if (rawProduct.extensions?.product_video?.video_url) {
    transformedProductVideo = {
      videoUrl: rawProduct.extensions.product_video.video_url,
    };
  }

  // Separate known extensions from other extensions
  const {
    product_instructions,
    product_video,
    product_subtitle,
    ...otherRawExtensions
  } = rawProduct.extensions || {};

  const finalExtensions: TransformedProduct["extensions"] = {};
  let hasAnyExtensions = false;

  if (transformedProductInstructions) {
    finalExtensions.productInstructions = transformedProductInstructions;
    hasAnyExtensions = true;
  }

  if (transformedProductVideo) {
    finalExtensions.productVideo = transformedProductVideo;
    hasAnyExtensions = true;
  }

  if (Object.keys(otherRawExtensions).length > 0) {
    Object.assign(finalExtensions, otherRawExtensions);
    hasAnyExtensions = true;
  }

  return {
    id: rawProduct.id,
    name: rawProduct.name,
    subtitle: product_subtitle?.subtitle,
    slug: rawProduct.slug,
    type: rawProduct.type,
    permalink: rawProduct.permalink,
    sku: rawProduct.sku,
    shortDescription: rawProduct.short_description,
    description: rawProduct.description,
    onSale: rawProduct.on_sale,
    prices,
    averageRating: parseFloat(rawProduct.average_rating) || 0,
    reviewCount: rawProduct.review_count,
    images: rawProduct.images,
    categories: transformTerms(rawProduct.categories),
    tags: transformTerms(rawProduct.tags),
    brands: transformTerms(rawProduct.brands),
    attributes: rawProduct.attributes,
    hasOptions: rawProduct.has_options,
    isPurchasable: rawProduct.is_purchasable,
    isInStock: rawProduct.is_in_stock,
    extensions: hasAnyExtensions ? finalExtensions : undefined,
  };
};

export const productApi = wcStoreApi.injectEndpoints({
  endpoints: (builder) => ({
    getProducts: builder.query<
      TransformedProductsResult,
      GetProductsParams | void
    >({
      query: (params: GetProductsParams = {}) => {
        const queryParams = new URLSearchParams();

        if (params.category && params.category.length)
          queryParams.append("category", params.category.join(","));
        if (params.tag && params.tag.length)
          queryParams.append("tag", params.tag.join(","));
        if (params.search) queryParams.append("search", params.search);
        if (params.page) queryParams.append("page", String(params.page));
        if (params.per_page)
          queryParams.append("per_page", String(params.per_page));
        if (params.slug) queryParams.append("slug", params.slug);
        queryParams.append("orderby", params.orderby || "menu_order");
        queryParams.append("order", params.order || "asc");

        return {
          url: "products",
          params: queryParams,
        };
      },
      transformResponse: (
        response: RawProductsResponse,
        meta: FetchBaseQueryMeta | undefined
      ): TransformedProductsResult => {
        const rawProducts: RawProduct[] = Array.isArray(response)
          ? response
          : [];

        const transformedProducts: TransformedProduct[] =
          rawProducts.map(transformRawProduct);

        const headers = meta?.response?.headers;
        const totalProducts = parseInt(headers?.get("X-WP-Total") || "0", 10);
        const totalPages = parseInt(headers?.get("X-WP-TotalPages") || "1", 10);

        return {
          products: transformedProducts,
          pagination: { totalProducts, totalPages },
        };
      },
      providesTags: (result) =>
        result?.products
          ? [
              ...result.products.map(({ id }) => ({
                type: "Product" as const,
                id,
              })),
              { type: "Product" as const, id: "LIST" },
            ]
          : [{ type: "Product" as const, id: "LIST" }],
    }),
    getProductBySlug: builder.query<TransformedProduct | undefined, string>({
      query: (slug: string) => ({
        url: "products",
        params: { slug },
      }),
      transformResponse: (
        response: RawProductsResponse,
        meta: FetchBaseQueryMeta | undefined
      ): TransformedProduct | undefined => {
        if (Array.isArray(response) && response.length > 0) {
          return transformRawProduct(response[0]);
        }
        return undefined;
      },
      providesTags: (result) =>
        result
          ? [{ type: "Product" as const, id: result.id }]
          : [{ type: "Product" as const, id: "NOT_FOUND" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetProductsQuery,
  useLazyGetProductsQuery,
  useGetProductBySlugQuery,
} = productApi;
